import { computed, makeObservable } from "mobx";
import { formatDate, ICommand } from "Application";
import { KeyValuePair, ViewModelBase } from "@shoothill/core";
import moment from "moment";

import { WeeklyRotaModel, WeeklyRotaModelValidator } from "./WeeklyRotaModel";
import { ManagersModule } from "./Managers";
import { WeeklyRotaDetailsViewModel } from "./WeeklyRotaDetail/WeeklyRotaDetailViewModel";

export class WeeklyRotaViewModel extends ViewModelBase<WeeklyRotaModel> {
    public managers: ManagersModule;

    constructor() {
        super(new WeeklyRotaModel());
        this.setValidator(new WeeklyRotaModelValidator());
        this.managers = new ManagersModule(this);
        makeObservable(this, {
            addWeeklyRotaAddButtonDisplayName: computed,
            sessionsLeftCount: computed,
            startDateDisplay: computed,
            hasWeeklyRota: computed,
            hasApiError: computed,
            showAddRotaSection: computed,
            showRotaLoader: computed,
        });
        this.model.startDate = moment(new Date()).startOf("isoWeek").toDate();
    }

    // ─── MobX state — delegated ──────────────────────────────────────────────

    public get isWeeklyRotaModalOpen(): boolean {
        return this.managers.mobx.isWeeklyRotaModalOpen;
    }
    public get copyWeeklyData(): boolean {
        return this.managers.mobx.copyWeeklyData;
    }
    public get successfullyGetWeeklyRota(): boolean {
        return this.managers.mobx.successfullyGetWeeklyRota;
    }
    public get weeklyRotaDetailViewModel(): WeeklyRotaDetailsViewModel {
        return this.managers.mobx.weeklyRotaDetailViewModel;
    }

    // ─── Domain data — delegated ─────────────────────────────────────────────

    public get formattedDateRange(): string {
        return this.managers.domain.formattedDateRange;
    }
    public get copyWeeklyDataOptions(): KeyValuePair[] {
        return this.managers.domain.copyWeeklyDataOptions;
    }
    public get addWeeklyRotaAddButtonDisplayName(): string {
        return this.managers.domain.addWeeklyRotaButtonDisplayName;
    }
    public get sessionsLeftCount(): number {
        return this.weeklyRotaDetailViewModel.model.leftStudentSession.length;
    }
    public get startDateDisplay(): string {
        return formatDate(this.getValue("startDate"));
    }
    public get hasWeeklyRota(): boolean {
        return !!this.weeklyRotaDetailViewModel.model.rotaId;
    }

    public get apiClient() {
        return this.managers.api.apiClient;
    }
    public get isLoading(): boolean {
        return this.managers.ui.isLoading;
    }
    public get isProcessing(): boolean {
        return this.managers.ui.isProcessing;
    }
    public get hasApiError(): boolean {
        return this.managers.api.apiClient.IsSubmitted && this.managers.api.apiClient.HaveValidationMessage;
    }
    public get showAddRotaSection(): boolean {
        return !this.hasWeeklyRota;
    }
    public get showRotaLoader(): boolean {
        // Show the loader on every rota fetch — including week navigation, where a rota is
        // already loaded (hasWeeklyRota === true) but the new week's data is still in flight.
        return this.isLoading;
    }

    // ─── API methods — delegated ─────────────────────────────────────────────

    public async loadRotaByDate(): Promise<void> {
        await this.managers.api.loadRotaByDate();
    }

    // ─── Commands — delegated ────────────────────────────────────────────────

    public get loadRotaCommand(): ICommand {
        return this.managers.commands.loadRotaCommand;
    }
    public get resetApiClientErrorCommand(): ICommand {
        return this.managers.commands.resetApiClientErrorCommand;
    }
    public get toggleWeeklyRotaModalOpenStateCommand(): ICommand {
        return this.managers.commands.toggleWeeklyRotaModalOpenStateCommand;
    }
    public get updateNextDateCommand(): ICommand {
        return this.managers.commands.updateNextDateCommand;
    }
    public get updatePreviousDateCommand(): ICommand {
        return this.managers.commands.updatePreviousDateCommand;
    }
    public get addWeeklyRotaCommandAsync(): ICommand {
        return this.managers.commands.addWeeklyRotaCommand;
    }
    public get copyWeeklyDataCommand(): ICommand {
        return this.managers.commands.copyWeeklyDataCommand;
    }
    public get updateRotaDateRangeCommand(): ICommand {
        return this.managers.commands.updateRotaDateRangeCommand;
    }
    public get updateAddRotaDateRangeCommand(): ICommand {
        return this.managers.commands.updateAddRotaDateRangeCommand;
    }

    // ─── Setters — kept for call-site compatibility ──────────────────────────

    public setIsWeeklyRotaModalOpen(value: boolean): void {
        this.managers.mobx.setIsWeeklyRotaModalOpen(value);
    }
    public setCopyWeeklyData(value: boolean): void {
        this.managers.mobx.setCopyWeeklyData(value);
        this.model.copyFromPreviousWeek = value;
    }
    public setSuccessfullyGetWeeklyRota(value: boolean): void {
        this.managers.mobx.setSuccessfullyGetWeeklyRota(value);
    }
}
